import React, { useEffect, useState } from "react";
import { User } from "firebase/auth";
import { useTranslation } from "react-i18next";
import { LeagueFixture, LeagueGwFixtures, fetchLeagueFixtures } from "../../api/leagues";
import { groupByDate, FixtureRow } from "./fixtureUtils";

interface Props {
  user: User;
  leagueId: string;
  resultsGwId: string | null;
  nextGwId: string | null;
  leagueStatus: "PREPARING" | "RUNNING" | "COMPLETED";
  highlightedClubIds?: Set<string>;
  leagueMode?: string;
}

const panelStyle: React.CSSProperties = {
  background: "var(--color-surface)",
  border: "1px solid var(--color-border-subtle)",
  borderRadius: 12,
  marginBottom: "1rem",
  overflow: "hidden",
};

function FixtureList({
  fixtures,
  highlightedClubIds,
  spMode,
  isResultsView,
}: {
  fixtures: LeagueFixture[];
  highlightedClubIds?: Set<string>;
  spMode: boolean;
  isResultsView: boolean;
}) {
  const groups = groupByDate(fixtures);
  if (groups.length === 0) return null;

  return (
    <>
      {groups.map((g) => (
        <div key={g.key}>
          <div style={{
            fontSize: "0.55rem",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: "var(--color-text-dim)",
            padding: "0.3rem 0.9rem 0.15rem",
            background: "var(--color-surface-alt)",
            borderBottom: "1px solid var(--color-border-subtle)",
          }}>
            {g.label}
          </div>
          {g.fixtures.map((f) => (
            <FixtureRow key={f.id} fixture={f} highlightedClubIds={highlightedClubIds} spMode={spMode} isResultsView={isResultsView} />
          ))}
        </div>
      ))}
    </>
  );
}

export default function FixturesPanel({ user, leagueId, resultsGwId, nextGwId, leagueStatus, highlightedClubIds, leagueMode }: Props) {
  const { t } = useTranslation();
  const [resultsData, setResultsData] = useState<LeagueGwFixtures | null>(null);
  const [nextData, setNextData] = useState<LeagueGwFixtures | null>(null);
  const [activeTab, setActiveTab] = useState<"results" | "next">("results");

  useEffect(() => {
    if (resultsGwId) {
      fetchLeagueFixtures(user, leagueId, resultsGwId).then(setResultsData).catch(() => {});
    } else {
      setResultsData(null);
    }
  }, [leagueId, resultsGwId]); // eslint-disable-line

  useEffect(() => {
    if (nextGwId) {
      fetchLeagueFixtures(user, leagueId, nextGwId).then(setNextData).catch(() => {});
    } else {
      setNextData(null);
    }
  }, [leagueId, nextGwId]); // eslint-disable-line

  // Default to results tab when results exist; fall back to next
  useEffect(() => {
    if (resultsData && (resultsData.gameweek.fixtures.length > 0)) {
      setActiveTab("results");
    } else if (nextData && nextData.gameweek.fixtures.length > 0) {
      setActiveTab("next");
    }
  }, [resultsData, nextData]);

  const showResults = !!resultsGwId && !!resultsData;
  const showNext = !!nextGwId && !!nextData && leagueStatus !== "COMPLETED";

  // Nothing to show
  if (!showResults && !showNext) return null;
  const hasResultsFixtures = (resultsData?.gameweek.fixtures.length ?? 0) > 0;
  const hasNextFixtures = (nextData?.gameweek.fixtures.length ?? 0) > 0;
  if (!hasResultsFixtures && !hasNextFixtures) return null;

  const hasTabs = showResults && showNext;
  const currentData = activeTab === "results" ? resultsData : nextData;
  const fixtures = currentData?.gameweek.fixtures ?? [];
  const spMode = leagueMode === "SCORE_PREDICTOR";
  const isResultsView = activeTab === "results";

  return (
    <div style={panelStyle}>
      {/* Header + tabs */}
      <div style={{ padding: "0.55rem 0.9rem 0" }}>
        {hasTabs ? (
          <div style={{ display: "flex", gap: "0.75rem" }}>
            {showResults && (
              <button
                onClick={() => setActiveTab("results")}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "0 0 0.4rem",
                  fontSize: "0.6rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: activeTab === "results" ? "var(--color-accent)" : "var(--color-text-dim)",
                  borderBottom: activeTab === "results" ? "2px solid var(--color-accent)" : "2px solid transparent",
                  fontFamily: "var(--font-body)",
                }}
              >
                {t("fixtures.results", { number: resultsData?.gameweek.number })}
              </button>
            )}
            {showNext && (
              <button
                onClick={() => setActiveTab("next")}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "0 0 0.4rem",
                  fontSize: "0.6rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: activeTab === "next" ? "var(--color-accent)" : "var(--color-text-dim)",
                  borderBottom: activeTab === "next" ? "2px solid var(--color-accent)" : "2px solid transparent",
                  fontFamily: "var(--font-body)",
                }}
              >
                {t("fixtures.nextUp", { number: nextData?.gameweek.number })}
              </button>
            )}
          </div>
        ) : (
          <span style={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-text-dim)" }}>
            {showResults
              ? t("fixtures.results", { number: resultsData?.gameweek.number })
              : t("fixtures.nextUp", { number: nextData?.gameweek.number })}
          </span>
        )}
      </div>

      {/* Fixtures */}
      <div style={{ marginTop: "0.35rem", maxHeight: "320px", overflowY: "auto" }}>
        {fixtures.length === 0 ? (
          <div style={{ padding: "0.4rem 0.9rem 0.6rem", fontSize: "0.72rem", color: "var(--color-text-dim)", fontStyle: "italic" }}>
            {t("fixtures.noFixtures")}
          </div>
        ) : (
          <FixtureList fixtures={fixtures} highlightedClubIds={highlightedClubIds} spMode={spMode} isResultsView={isResultsView} />
        )}
      </div>
    </div>
  );
}
